/**
 * Multi-org: list memberships and switch active organization (JWT org claim).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/stores/use-auth';
import { PROFILE_QUERY_KEY } from '@/hooks/useProfile';
import { mapApiUserProfileToStore } from '@/lib/map-api-user-profile';
import type { ApiUserProfile } from '@/lib/map-api-user-profile';

export const MY_ORGANIZATIONS_QUERY_KEY = ['users', 'me', 'organizations'] as const;

export interface MyOrganizationRow {
  organization_id: string;
  organization_name: string;
  role: string;
  is_primary: boolean;
}

async function fetchMyOrganizations(): Promise<MyOrganizationRow[]> {
  const res = await api.get<{ organizations: MyOrganizationRow[] }>('/users/me/organizations');
  return res.organizations;
}

/** Shared options so imperative callers (`queryClient.fetchQuery`) hit the same cache entry. */
export function myOrganizationsQueryOptions() {
  return {
    queryKey: MY_ORGANIZATIONS_QUERY_KEY,
    queryFn: fetchMyOrganizations,
    staleTime: 60 * 1000,
  } as const;
}

export function useMyOrganizations(enabled = true) {
  return useQuery({
    ...myOrganizationsQueryOptions(),
    enabled,
  });
}

interface SwitchResponse {
  accessToken: string;
  expiresIn: number;
}

async function switchOrgApi(organizationId: string): Promise<SwitchResponse> {
  return api.post<SwitchResponse>('/users/me/active-organization', {
    organization_id: organizationId,
  });
}

export function useSwitchActiveOrganization() {
  const queryClient = useQueryClient();
  const setSessionToken = useAuth((s) => s.setSessionToken);
  const setUser = useAuth((s) => s.setUser);

  return useMutation({
    mutationFn: switchOrgApi,
    onSuccess: async (data) => {
      setSessionToken(data.accessToken);
      const apiUser = await api.get<ApiUserProfile>('/users/me');
      const user = mapApiUserProfileToStore(apiUser);
      setUser(user, data.accessToken);
      await queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: MY_ORGANIZATIONS_QUERY_KEY });
      await queryClient.invalidateQueries();
    },
  });
}
